import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Home, Car, Truck, Sofa, Plus, Upload, Edit, Trash2, Eye, 
  Search, Filter, ChevronDown, ChevronRight, Globe, Loader2,
  CheckCircle, X, FileSpreadsheet, Download, ExternalLink
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { api } from '../../lib/auth';
import { toast } from 'sonner';

const EXTERNAL_DIRECTORY_URL = process.env.REACT_APP_EXTERNAL_DIRECTORY_URL || 'https://search.aihorsepowered.com';

// Inventory type configurations
const INVENTORY_TYPES = [
  {
    id: 'real-estate',
    name: 'Real Estate',
    icon: Home,
    color: 'from-violet-500 to-purple-600',
    description: 'Properties for realtors, builders, landlords, investors',
    endpoint: '/api/directory/inventory/real-estate',
    idField: 'listing_id',
    titleField: 'property_title',
    priceField: 'price',
    statusField: 'status',
    imageField: 'primary_image_url',
    csvTemplate: 'property_title,property_type,status,price,street_address,city,state,zip_code,bedrooms,bathrooms,square_feet,description',
    fields: [
      { name: 'property_title', label: 'Property Title', type: 'text', required: true, placeholder: 'e.g., Beautiful 3BR Home in Downtown' },
      { name: 'property_type', label: 'Property Type', type: 'select', required: true, options: ['single_family', 'condo', 'townhouse', 'multi_family', 'land', 'commercial'] },
      { name: 'status', label: 'Status', type: 'select', required: true, options: ['active', 'pending', 'sold', 'off_market'], default: 'active' },
      { name: 'price', label: 'Price ($)', type: 'number', required: true, placeholder: '450000' },
      { name: 'street_address', label: 'Street Address', type: 'text', required: true, placeholder: '123 Main St' },
      { name: 'city', label: 'City', type: 'text', required: true, placeholder: 'Miami' },
      { name: 'state', label: 'State', type: 'text', required: true, placeholder: 'FL' },
      { name: 'zip_code', label: 'ZIP Code', type: 'text', required: true, placeholder: '33101' },
      { name: 'county', label: 'County', type: 'text', placeholder: 'Miami-Dade' },
      { name: 'bedrooms', label: 'Bedrooms', type: 'number', placeholder: '3' },
      { name: 'bathrooms', label: 'Bathrooms', type: 'number', placeholder: '2', step: '0.5' },
      { name: 'square_feet', label: 'Square Feet', type: 'number', placeholder: '1800' },
      { name: 'lot_size', label: 'Lot Size', type: 'text', placeholder: '0.25 acres' },
      { name: 'year_built', label: 'Year Built', type: 'number', placeholder: '2010' },
      { name: 'garage_spaces', label: 'Garage Spaces', type: 'number', placeholder: '2' },
      { name: 'stories', label: 'Stories', type: 'number', placeholder: '2' },
      { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Describe the property...' },
      { name: 'features', label: 'Features (comma-separated)', type: 'text', placeholder: 'Pool, Hardwood Floors, Updated Kitchen' },
      { name: 'neighborhood', label: 'Neighborhood', type: 'text', placeholder: 'Downtown' },
      { name: 'school_district', label: 'School District', type: 'text', placeholder: 'Miami-Dade County Schools' },
      { name: 'hoa', label: 'HOA Fee (Monthly)', type: 'number', placeholder: '350' },
      { name: 'property_taxes', label: 'Property Taxes (Annual)', type: 'number', placeholder: '5400' },
      { name: 'virtual_tour_url', label: 'Virtual Tour URL', type: 'text', placeholder: 'https://...' },
      { name: 'primary_image_url', label: 'Primary Image URL', type: 'text', placeholder: 'https://...' },
      { name: 'image_urls', label: 'Image Gallery (up to 30 images)', type: 'images', placeholder: 'Add image URLs for carousel' },
      { name: 'video_url', label: 'Video URL', type: 'text', placeholder: 'https://youtube.com/...' },
      { name: 'floorplan_url', label: 'Floorplan URL', type: 'text', placeholder: 'https://...' },
      { name: 'agent_name', label: 'Agent Name', type: 'text', placeholder: 'John Smith' },
      { name: 'agent_phone', label: 'Agent Phone', type: 'text', placeholder: '(555) 123-4567' },
      { name: 'agent_email', label: 'Agent Email', type: 'email', placeholder: 'agent@example.com' },
      { name: 'brokerage_name', label: 'Brokerage Name', type: 'text', placeholder: 'RE/MAX' },
      { name: 'brokerage_website', label: 'Brokerage Website', type: 'text', placeholder: 'https://...' },
      { name: 'get_financing_url', label: 'Get Financing Link', type: 'text', placeholder: 'https://your-financing-partner.com' },
      { name: 'keywords', label: 'Keywords (comma-separated)', type: 'text', placeholder: 'waterfront, luxury, new construction' },
    ]
  },
  {
    id: 'vehicles',
    name: 'Vehicles',
    icon: Car,
    color: 'from-blue-500 to-cyan-600',
    description: 'Auto, truck, motorcycle inventory for dealers',
    endpoint: '/api/directory/inventory/vehicles',
    idField: 'inventory_id',
    titleField: 'vehicle_title',
    priceField: 'price',
    statusField: 'condition',
    imageField: 'primary_image_url',
    csvTemplate: 'vehicle_title,condition,year,make,model,trim,mileage,vin,price,city,state,zip_code,description',
    fields: [
      { name: 'vehicle_title', label: 'Vehicle Title', type: 'text', required: true, placeholder: 'e.g., 2023 Toyota Camry XLE' },
      { name: 'condition', label: 'Condition', type: 'select', required: true, options: ['new', 'used', 'certified'], default: 'used' },
      { name: 'year', label: 'Year', type: 'number', required: true, placeholder: '2023' },
      { name: 'make', label: 'Make', type: 'text', required: true, placeholder: 'Toyota' },
      { name: 'model', label: 'Model', type: 'text', required: true, placeholder: 'Camry' },
      { name: 'trim', label: 'Trim', type: 'text', placeholder: 'XLE' },
      { name: 'body_style', label: 'Body Style', type: 'select', options: ['sedan', 'suv', 'truck', 'coupe', 'convertible', 'wagon', 'van', 'hatchback', 'motorcycle'] },
      { name: 'mileage', label: 'Mileage', type: 'number', placeholder: '25000' },
      { name: 'vin', label: 'VIN', type: 'text', placeholder: '1HGBH41JXMN109186' },
      { name: 'stock_number', label: 'Stock Number', type: 'text', placeholder: 'STK12345' },
      { name: 'engine', label: 'Engine', type: 'text', placeholder: '2.5L 4-Cylinder' },
      { name: 'transmission', label: 'Transmission', type: 'select', options: ['automatic', 'manual', 'cvt'] },
      { name: 'drivetrain', label: 'Drivetrain', type: 'select', options: ['fwd', 'rwd', 'awd', '4wd'] },
      { name: 'fuel_type', label: 'Fuel Type', type: 'select', options: ['gasoline', 'diesel', 'electric', 'hybrid', 'plug-in hybrid'] },
      { name: 'exterior_color', label: 'Exterior Color', type: 'text', placeholder: 'Midnight Black' },
      { name: 'interior_color', label: 'Interior Color', type: 'text', placeholder: 'Tan Leather' },
      { name: 'price', label: 'Price ($)', type: 'number', required: true, placeholder: '32500' },
      { name: 'msrp', label: 'MSRP ($)', type: 'number', placeholder: '35000' },
      { name: 'sale_price', label: 'Sale Price ($)', type: 'number', placeholder: '30995' },
      { name: 'financing_available', label: 'Financing Available', type: 'checkbox', default: true },
      { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Describe the vehicle...' },
      { name: 'features', label: 'Features (comma-separated)', type: 'text', placeholder: 'Leather, Sunroof, Navigation' },
      { name: 'vehicle_history', label: 'Vehicle History', type: 'text', placeholder: 'One owner, clean title' },
      { name: 'warranty', label: 'Warranty', type: 'text', placeholder: '3yr/36k mile bumper to bumper' },
      { name: 'certified_preowned', label: 'Certified Pre-Owned', type: 'checkbox' },
      { name: 'primary_image_url', label: 'Primary Image URL', type: 'text', placeholder: 'https://...' },
      { name: 'image_urls', label: 'Image Gallery (up to 30 images)', type: 'images', placeholder: 'Add image URLs for carousel' },
      { name: 'video_url', label: 'Video URL', type: 'text', placeholder: 'https://youtube.com/...' },
      { name: 'dealership_name', label: 'Dealership Name', type: 'text', placeholder: 'ABC Motors' },
      { name: 'dealership_address', label: 'Dealership Address', type: 'text', placeholder: '456 Auto Row' },
      { name: 'city', label: 'City', type: 'text', required: true, placeholder: 'Miami' },
      { name: 'state', label: 'State', type: 'text', required: true, placeholder: 'FL' },
      { name: 'zip_code', label: 'ZIP Code', type: 'text', required: true, placeholder: '33101' },
      { name: 'dealer_phone', label: 'Dealer Phone', type: 'text', placeholder: '(555) 123-4567' },
      { name: 'dealer_website', label: 'Dealer Website', type: 'text', placeholder: 'https://...' },
      { name: 'get_financing_url', label: 'Get Financing Link', type: 'text', placeholder: 'https://your-financing-partner.com' },
      { name: 'keywords', label: 'Keywords (comma-separated)', type: 'text', placeholder: 'low miles, one owner, certified' },
    ]
  },
  {
    id: 'rvs',
    name: 'RVs',
    icon: Truck,
    color: 'from-emerald-500 to-teal-600',
    description: 'RV inventory for dealers and travel centers',
    endpoint: '/api/directory/inventory/rvs',
    idField: 'rv_id',
    titleField: 'rv_title',
    priceField: 'price',
    statusField: 'condition',
    imageField: 'primary_image_url',
    csvTemplate: 'rv_title,condition,year,make,model,rv_type,length,sleeps,price,city,state,zip_code,description',
    fields: [
      { name: 'rv_title', label: 'RV Title', type: 'text', required: true, placeholder: 'e.g., 2022 Winnebago Vista 31BE' },
      { name: 'condition', label: 'Condition', type: 'select', required: true, options: ['new', 'used'], default: 'used' },
      { name: 'year', label: 'Year', type: 'number', required: true, placeholder: '2022' },
      { name: 'make', label: 'Make', type: 'text', required: true, placeholder: 'Winnebago' },
      { name: 'model', label: 'Model', type: 'text', required: true, placeholder: 'Vista 31BE' },
      { name: 'rv_type', label: 'RV Type', type: 'select', required: true, options: ['class_a', 'class_b', 'class_c', 'travel_trailer', 'fifth_wheel', 'toy_hauler', 'pop_up'] },
      { name: 'length', label: 'Length', type: 'text', placeholder: '32 ft' },
      { name: 'sleeps', label: 'Sleeps', type: 'number', placeholder: '6' },
      { name: 'slide_outs', label: 'Slide Outs', type: 'number', placeholder: '2' },
      { name: 'fuel_type', label: 'Fuel Type', type: 'select', options: ['gasoline', 'diesel'] },
      { name: 'engine', label: 'Engine', type: 'text', placeholder: 'Ford V10' },
      { name: 'transmission', label: 'Transmission', type: 'select', options: ['automatic', 'manual'] },
      { name: 'mileage', label: 'Mileage', type: 'number', placeholder: '15000' },
      { name: 'vin', label: 'VIN', type: 'text', placeholder: '1FVACWDT5LHAA1234' },
      { name: 'price', label: 'Price ($)', type: 'number', required: true, placeholder: '125000' },
      { name: 'sale_price', label: 'Sale Price ($)', type: 'number', placeholder: '119995' },
      { name: 'financing_available', label: 'Financing Available', type: 'checkbox', default: true },
      { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Describe the RV...' },
      { name: 'features', label: 'Features (comma-separated)', type: 'text', placeholder: 'Generator, Leveling System, Solar Panels' },
      { name: 'bathroom_type', label: 'Bathroom Type', type: 'select', options: ['full', 'half', 'outdoor_shower', 'none'] },
      { name: 'kitchen_features', label: 'Kitchen Features (comma-separated)', type: 'text', placeholder: 'Microwave, Stove, Refrigerator' },
      { name: 'climate_control', label: 'Climate Control', type: 'select', options: ['ac', 'heater', 'both', 'none'] },
      { name: 'primary_image_url', label: 'Primary Image URL', type: 'text', placeholder: 'https://...' },
      { name: 'image_urls', label: 'Image Gallery (up to 30 images)', type: 'images', placeholder: 'Add image URLs for carousel' },
      { name: 'video_url', label: 'Video URL', type: 'text', placeholder: 'https://youtube.com/...' },
      { name: 'dealership_name', label: 'Dealership Name', type: 'text', placeholder: 'RV World' },
      { name: 'dealership_address', label: 'Dealership Address', type: 'text', placeholder: '789 RV Lane' },
      { name: 'city', label: 'City', type: 'text', required: true, placeholder: 'Orlando' },
      { name: 'state', label: 'State', type: 'text', required: true, placeholder: 'FL' },
      { name: 'zip_code', label: 'ZIP Code', type: 'text', required: true, placeholder: '32801' },
      { name: 'dealer_phone', label: 'Dealer Phone', type: 'text', placeholder: '(555) 123-4567' },
      { name: 'dealer_website', label: 'Dealer Website', type: 'text', placeholder: 'https://...' },
      { name: 'get_financing_url', label: 'Get Financing Link', type: 'text', placeholder: 'https://your-financing-partner.com' },
      { name: 'keywords', label: 'Keywords (comma-separated)', type: 'text', placeholder: 'family rv, bunkhouse, outdoor kitchen' },
    ]
  },
  {
    id: 'furniture',
    name: 'Furniture',
    icon: Sofa,
    color: 'from-amber-500 to-orange-600',
    description: 'Furniture catalog for stores and showrooms',
    endpoint: '/api/directory/inventory/furniture',
    idField: 'product_id',
    titleField: 'product_name',
    priceField: 'price',
    statusField: 'condition',
    imageField: 'primary_image_url',
    csvTemplate: 'product_name,category,subcategory,style,price,material,dimensions,color,condition,description',
    fields: [
      { name: 'product_name', label: 'Product Name', type: 'text', required: true, placeholder: 'e.g., Modern Leather Sectional Sofa' },
      { name: 'category', label: 'Category', type: 'select', required: true, options: ['living_room', 'bedroom', 'dining', 'office', 'outdoor', 'kids'] },
      { name: 'subcategory', label: 'Subcategory', type: 'text', placeholder: 'sofa, bed, table, chair, desk' },
      { name: 'style', label: 'Style', type: 'select', options: ['modern', 'traditional', 'contemporary', 'rustic', 'industrial', 'mid-century', 'bohemian'] },
      { name: 'room_type', label: 'Room Type', type: 'text', placeholder: 'Living Room' },
      { name: 'price', label: 'Price ($)', type: 'number', required: true, placeholder: '1299' },
      { name: 'sale_price', label: 'Sale Price ($)', type: 'number', placeholder: '999' },
      { name: 'material', label: 'Material', type: 'text', placeholder: 'Top Grain Leather' },
      { name: 'dimensions', label: 'Dimensions', type: 'text', placeholder: '84W x 36D x 32H inches' },
      { name: 'color', label: 'Color', type: 'text', placeholder: 'Charcoal Gray' },
      { name: 'finish', label: 'Finish', type: 'text', placeholder: 'Matte' },
      { name: 'condition', label: 'Condition', type: 'select', options: ['new', 'like_new', 'used'], default: 'new' },
      { name: 'description', label: 'Description', type: 'textarea', placeholder: 'Describe the furniture...' },
      { name: 'features', label: 'Features (comma-separated)', type: 'text', placeholder: 'Reclining, USB Ports, Storage' },
      { name: 'care_instructions', label: 'Care Instructions', type: 'text', placeholder: 'Wipe clean with damp cloth' },
      { name: 'warranty', label: 'Warranty', type: 'text', placeholder: '5-year limited warranty' },
      { name: 'primary_image_url', label: 'Primary Image URL', type: 'text', placeholder: 'https://...' },
      { name: 'image_urls', label: 'Image Gallery (up to 30 images)', type: 'images', placeholder: 'Add image URLs for carousel' },
      { name: 'video_url', label: 'Video URL', type: 'text', placeholder: 'https://youtube.com/...' },
      { name: 'store_name', label: 'Store Name', type: 'text', placeholder: 'Furniture Gallery' },
      { name: 'store_address', label: 'Store Address', type: 'text', placeholder: '100 Showroom Blvd' },
      { name: 'city', label: 'City', type: 'text', required: true, placeholder: 'Dallas' },
      { name: 'state', label: 'State', type: 'text', required: true, placeholder: 'TX' },
      { name: 'zip_code', label: 'ZIP Code', type: 'text', required: true, placeholder: '75201' },
      { name: 'store_phone', label: 'Store Phone', type: 'text', placeholder: '(555) 123-4567' },
      { name: 'store_website', label: 'Store Website', type: 'text', placeholder: 'https://...' },
      { name: 'get_financing_url', label: 'Get Financing Link', type: 'text', placeholder: 'https://your-financing-partner.com' },
      { name: 'keywords', label: 'Keywords (comma-separated)', type: 'text', placeholder: 'sectional, leather, modern, living room' },
    ]
  }
];

// Single listing form component
function ListingForm({ type, listing, onSave, onCancel }) {
  const [formData, setFormData] = useState(listing || {});
  const [saving, setSaving] = useState(false);
  
  const config = INVENTORY_TYPES.find(t => t.id === type);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Convert comma-separated fields to arrays
      const processedData = { ...formData };
      ['features', 'keywords', 'city_service_area', 'kitchen_features'].forEach(field => {
        if (processedData[field] && typeof processedData[field] === 'string') {
          processedData[field] = processedData[field].split(',').map(s => s.trim()).filter(Boolean);
        }
      });
      
      await onSave(processedData);
    } finally {
      setSaving(false);
    }
  };
  
  const updateField = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {config.fields.map(field => (
          <div key={field.name} className={field.type === 'textarea' || field.type === 'images' ? 'col-span-full' : ''}>
            <label className="text-sm text-gray-400 mb-1 block">
              {field.label} {field.required && <span className="text-red-400">*</span>}
            </label>
            {field.type === 'select' ? (
              <select
                value={formData[field.name] || field.default || ''}
                onChange={(e) => updateField(field.name, e.target.value)}
                required={field.required}
                className="w-full bg-[#0a0f1a] border border-white/10 text-white rounded-lg px-3 py-2 text-sm [&>option]:bg-[#0a0f1a] [&>option]:text-white"
                style={{ backgroundColor: '#0a0f1a' }}
              >
                <option value="" className="bg-[#0a0f1a] text-gray-400">Select {field.label}</option>
                {field.options.map(opt => (
                  <option key={opt} value={opt} className="bg-[#0a0f1a] text-white">{opt.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</option>
                ))}
              </select>
            ) : field.type === 'textarea' ? (
              <textarea
                value={formData[field.name] || ''}
                onChange={(e) => updateField(field.name, e.target.value)}
                placeholder={field.placeholder}
                rows={3}
                className="w-full bg-[#0a0f1a] border border-white/10 text-white rounded-lg px-3 py-2 text-sm placeholder:text-gray-500"
              />
            ) : field.type === 'checkbox' ? (
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData[field.name] ?? field.default ?? false}
                  onChange={(e) => updateField(field.name, e.target.checked)}
                  className="rounded border-gray-500 bg-[#0a0f1a]"
                />
                <span className="text-sm text-gray-300">Yes</span>
              </label>
            ) : field.type === 'images' ? (
              <div className="space-y-2">
                <p className="text-xs text-gray-500">Add up to 30 image URLs (one per line)</p>
                <textarea
                  value={(formData[field.name] || []).join('\n')}
                  onChange={(e) => updateField(field.name, e.target.value.split('\n').filter(url => url.trim()))}
                  placeholder="https://example.com/image1.jpg&#10;https://example.com/image2.jpg&#10;..."
                  rows={4}
                  className="w-full bg-[#0a0f1a] border border-white/10 text-white rounded-lg px-3 py-2 text-sm font-mono placeholder:text-gray-500"
                />
                <p className="text-xs text-gray-500">{(formData[field.name] || []).length}/30 images</p>
              </div>
            ) : (
              <Input
                type={field.type}
                value={formData[field.name] || ''}
                onChange={(e) => updateField(field.name, field.type === 'number' ? (e.target.value ? parseFloat(e.target.value) : '') : e.target.value)}
                placeholder={field.placeholder}
                required={field.required}
                step={field.step}
                className="bg-[#0a0f1a] border-white/10 text-white text-sm placeholder:text-gray-500"
              />
            )}
          </div>
        ))}
      </div>
      
      <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={saving} className="bg-gradient-to-r from-cyan-500 to-blue-500">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
          {listing?.id ? 'Update Listing' : 'Create Listing'}
        </Button>
      </div>
    </form>
  );
}

// Bulk upload component
function BulkUpload({ type, onComplete }) {
  const [uploading, setUploading] = useState(false);
  const [csvData, setCsvData] = useState('');
  const [results, setResults] = useState(null);
  
  const config = INVENTORY_TYPES.find(t => t.id === type);
  
  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim());
    const data = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      const row = {};
      headers.forEach((header, idx) => {
        const value = values[idx] || '';
        // Convert numbers
        if (['price', 'msrp', 'sale_price', 'year', 'mileage', 'bedrooms', 'bathrooms', 'square_feet', 'hoa', 'property_taxes', 'sleeps', 'slide_outs'].includes(header)) {
          row[header] = value ? parseFloat(value) : null;
        } else {
          row[header] = value;
        }
      });
      if (row[config.titleField]) { // Only add if title exists
        data.push(row);
      }
    }
    return data;
  };
  
  const handleBulkUpload = async () => {
    const listings = parseCSV(csvData);
    if (listings.length === 0) {
      toast.error('No valid listings found in CSV');
      return;
    }
    
    setUploading(true);
    try {
      const response = await api.post(`${config.endpoint}/bulk`, listings);
      setResults(response.data);
      if (response.data.successful > 0) {
        toast.success(`Successfully uploaded ${response.data.successful} listings`);
        onComplete?.();
      }
      if (response.data.failed > 0) {
        toast.error(`${response.data.failed} listings failed`);
      }
    } catch (error) {
      toast.error('Bulk upload failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };
  
  const downloadTemplate = () => {
    const blob = new Blob([config.csvTemplate], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${type}-template.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-400">
          Paste your CSV data below or download the template to get started.
        </p>
        <Button variant="outline" size="sm" onClick={downloadTemplate}>
          <Download className="w-4 h-4 mr-2" />
          Download Template
        </Button>
      </div>
      
      <textarea
        value={csvData}
        onChange={(e) => setCsvData(e.target.value)}
        placeholder={`Paste CSV data here...\n\nExample header row:\n${config.csvTemplate}`}
        rows={10}
        className="w-full bg-black/30 border border-white/10 text-white rounded-lg px-4 py-3 font-mono text-sm"
      />
      
      <div className="flex justify-between items-center">
        <p className="text-xs text-gray-500">
          {csvData ? `${parseCSV(csvData).length} listings detected` : 'No data'}
        </p>
        <Button 
          onClick={handleBulkUpload} 
          disabled={uploading || !csvData}
          className="bg-gradient-to-r from-emerald-500 to-teal-500"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
          Upload {parseCSV(csvData).length} Listings
        </Button>
      </div>
      
      {results && (
        <div className={`p-4 rounded-lg ${results.failed > 0 ? 'bg-amber-900/30 border border-amber-500/30' : 'bg-emerald-900/30 border border-emerald-500/30'}`}>
          <p className="font-medium text-white mb-2">Upload Results</p>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-400">Total</p>
              <p className="text-white font-semibold">{results.total_submitted}</p>
            </div>
            <div>
              <p className="text-emerald-400">Successful</p>
              <p className="text-emerald-300 font-semibold">{results.successful}</p>
            </div>
            <div>
              <p className="text-red-400">Failed</p>
              <p className="text-red-300 font-semibold">{results.failed}</p>
            </div>
          </div>
          {results.errors?.length > 0 && (
            <div className="mt-3 pt-3 border-t border-white/10">
              <p className="text-sm text-red-400 mb-2">Errors:</p>
              <ul className="text-xs text-gray-400 space-y-1 max-h-32 overflow-y-auto">
                {results.errors.map((err, idx) => (
                  <li key={idx}>Row {err.row}: {err.error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Main inventory listings component
export function InventoryListings() {
  const [selectedType, setSelectedType] = useState(null);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [editingListing, setEditingListing] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedType, setExpandedType] = useState(null);
  
  const loadListings = async (type) => {
    if (!type) return;
    setLoading(true);
    try {
      const config = INVENTORY_TYPES.find(t => t.id === type);
      const response = await api.get(config.endpoint);
      setListings(response.data.listings || []);
    } catch (error) {
      console.error('Failed to load listings:', error);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (selectedType) {
      loadListings(selectedType);
    }
  }, [selectedType]);
  
  const handleSaveListing = async (data) => {
    const config = INVENTORY_TYPES.find(t => t.id === selectedType);
    try {
      if (editingListing?.id) {
        await api.put(`${config.endpoint}/${editingListing.id}`, data);
        toast.success('Listing updated successfully');
      } else {
        await api.post(config.endpoint, data);
        toast.success('Listing created successfully');
      }
      setShowForm(false);
      setEditingListing(null);
      loadListings(selectedType);
    } catch (error) {
      toast.error('Failed to save listing: ' + (error.response?.data?.detail || error.message));
    }
  };
  
  const handleDeleteListing = async (listing) => {
    if (!confirm('Are you sure you want to delete this listing?')) return;
    const config = INVENTORY_TYPES.find(t => t.id === selectedType);
    try {
      await api.delete(`${config.endpoint}/${listing.id}`);
      toast.success('Listing deleted');
      loadListings(selectedType);
    } catch (error) {
      toast.error('Failed to delete listing');
    }
  };
  
  const handlePublishListing = async (listing) => {
    const config = INVENTORY_TYPES.find(t => t.id === selectedType);
    try {
      await api.post(`${config.endpoint}/${listing.id}/publish`);
      toast.success('Listing published to directory');
      loadListings(selectedType);
    } catch (error) {
      toast.error('Failed to publish listing');
    }
  };
  
  const getConfig = () => INVENTORY_TYPES.find(t => t.id === selectedType);
  
  const filteredListings = listings.filter(listing => {
    if (!searchQuery) return true;
    const config = getConfig();
    const title = listing[config?.titleField] || '';
    return title.toLowerCase().includes(searchQuery.toLowerCase());
  });
  
  // If no type selected, show the type selection grid
  if (!selectedType) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Inventory Listings</h2>
            <p className="text-sm text-gray-400 mt-1">Upload single or bulk listings for your business</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {INVENTORY_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <motion.div
                key={type.id}
                whileHover={{ scale: 1.02 }}
                onClick={() => setSelectedType(type.id)}
                className={`bg-gradient-to-br ${type.color} p-[1px] rounded-xl cursor-pointer`}
              >
                <div className="bg-[#0a0f1a] rounded-xl p-6 h-full hover:bg-[#0d1525] transition-colors">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${type.color} flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white text-lg">{type.name}</h3>
                      <p className="text-sm text-gray-400 mt-1">{type.description}</p>
                      <div className="flex items-center gap-2 mt-3">
                        <span className="text-xs px-2 py-1 bg-white/10 rounded text-gray-300">Single Upload</span>
                        <span className="text-xs px-2 py-1 bg-white/10 rounded text-gray-300">Bulk CSV</span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-500" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
        
        <div className="p-4 bg-cyan-900/20 border border-cyan-500/30 rounded-lg">
          <p className="text-sm text-cyan-300">
            <strong>💡 Pro Tip:</strong> Each listing becomes a searchable page in the AI Business Finder directory. 
            Use bulk upload to import your entire inventory from a spreadsheet.
          </p>
        </div>
      </div>
    );
  }
  
  const config = getConfig();
  const Icon = config.icon;
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            onClick={() => { setSelectedType(null); setShowForm(false); setShowBulkUpload(false); }}
            className="text-gray-400 hover:text-white"
          >
            ← Back
          </Button>
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${config.color} flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">{config.name} Listings</h2>
            <p className="text-sm text-gray-400">{listings.length} listings total</p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => { setShowBulkUpload(!showBulkUpload); setShowForm(false); }}
            className={showBulkUpload ? 'border-emerald-500 text-emerald-400' : ''}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Bulk Upload
          </Button>
          <Button 
            onClick={() => { setShowForm(true); setShowBulkUpload(false); setEditingListing(null); }}
            className={`bg-gradient-to-r ${config.color}`}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Single
          </Button>
        </div>
      </div>
      
      {/* Bulk Upload Panel */}
      <AnimatePresence>
        {showBulkUpload && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-[#0d1525] rounded-xl p-6 border border-white/10"
          >
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              <Upload className="w-5 h-5 text-emerald-400" />
              Bulk {config.name} Upload
            </h3>
            <BulkUpload 
              type={selectedType} 
              onComplete={() => { loadListings(selectedType); setShowBulkUpload(false); }} 
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Single Listing Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-[#0d1525] rounded-xl p-6 border border-white/10"
          >
            <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
              {editingListing ? <Edit className="w-5 h-5 text-amber-400" /> : <Plus className="w-5 h-5 text-cyan-400" />}
              {editingListing ? 'Edit' : 'Add New'} {config.name} Listing
            </h3>
            <ListingForm 
              type={selectedType}
              listing={editingListing}
              onSave={handleSaveListing}
              onCancel={() => { setShowForm(false); setEditingListing(null); }}
            />
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Search Bar */}
      {!showForm && !showBulkUpload && listings.length > 0 && (
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={`Search ${config.name.toLowerCase()}...`}
              className="pl-10 bg-white/5 border-white/10 text-white"
            />
          </div>
        </div>
      )}
      
      {/* Listings Grid */}
      {!showForm && !showBulkUpload && (
        <div className="space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
            </div>
          ) : filteredListings.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Icon className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No {config.name.toLowerCase()} listings yet</p>
              <p className="text-sm mt-1">Click &quot;Add Single&quot; or &quot;Bulk Upload&quot; to get started</p>
            </div>
          ) : (
            filteredListings.map(listing => (
              <motion.div
                key={listing.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#0d1525] rounded-xl p-4 border border-white/10 hover:border-white/20 transition-colors"
              >
                <div className="flex items-center gap-4">
                  {/* Image */}
                  <div className="w-20 h-20 rounded-lg bg-white/5 overflow-hidden flex-shrink-0">
                    {listing[config.imageField] ? (
                      <img 
                        src={listing[config.imageField]} 
                        alt={listing[config.titleField]}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon className="w-8 h-8 text-gray-600" />
                      </div>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-white truncate">{listing[config.titleField]}</h4>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        listing.publish_status === 'published' 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {listing.publish_status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">
                      {listing.city}, {listing.state} • ${listing[config.priceField]?.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      ID: {listing[config.idField]} • {listing.views || 0} views
                    </p>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {listing.publish_status !== 'published' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handlePublishListing(listing)}
                        className="text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10"
                      >
                        <Globe className="w-4 h-4 mr-1" />
                        Publish
                      </Button>
                    )}
                    {listing.publish_status === 'published' && (
                      <a
                        href={`${EXTERNAL_DIRECTORY_URL}/listing/${listing.slug || listing.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                      >
                        <ExternalLink className="w-3 h-3" />
                        View
                      </a>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setEditingListing(listing); setShowForm(true); }}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteListing(listing)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default InventoryListings;
